'use client';

import { c as _c } from "react/compiler-runtime";
import { toCustomGateway } from '@morfi/backend-sdk-v2/client';
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { preload } from 'react-dom';
import { OptimizedImage } from '@/components/images/optimized-image';
import { OutcomeCard } from '@/components/markets/outcome-card';
import { Panel, PanelContent } from '@/components/ui/panel';
import { resolveProposalCategory } from '@/config/proposal-display';
import { MOTION_HOVER, MOTION_POSITION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OutcomeItem {
  index: number;
  label: string;
  imageUrl: string;
  imageNsfw: boolean;
}
interface ProposalOutcomesPanelProps {
  proposalPubkey: string;
  proposalCategory: string;
  outcomes: OutcomeItem[];
  className?: string;
  /** Page-owned — `SuggestOutcomeModal` must not sit in the main column or `Sheet.Root` stays as a zero-height flex item and doubles `gap-4` before the next section (desktop). */
  onSuggestModalOpenChange: (open: boolean) => void;
  isCreator?: boolean;
  /** When true the viewer is logged out — clicking the link should
   *  open the login modal instead of the wizard. */
  isLoggedOut?: boolean;
  /** Opens the login modal — used when isLoggedOut and the user clicks
   *  the Suggest outcome link. Threaded from `handleConnectWallet`. */
  onRequireSignIn?: () => void;
}

// ---------------------------------------------------------------------------
// Motion config (module-scope per React Compiler rule)
// ---------------------------------------------------------------------------

// Decorative ambient behind hover — fades via MOTION_HOVER (150 ms, DURATION_HOVER_MS +
// blueprint ease-default). Holds at HOVER_AMBIENT_OPACITY so panel typography stays primary.
const HOVER_AMBIENT_OPACITY = 0.8 as const;
const FALLBACK_EMOJI = '\u{1F4A1}';
const FADE_VARIANTS = {
  initial: {
    opacity: 0
  },
  animate: {
    opacity: HOVER_AMBIENT_OPACITY
  },
  exit: {
    opacity: 0
  }
} as const;

// Radial mask anchored at the top-right corner — image is fully opaque near the
// corner and softly fades to transparent toward the bottom-left, dissolving into
// the panel surface on every side without a visible gradient seam. Mask
// gradients are luminance/alpha stops (black = visible, transparent = hidden),
// not theme colours, so the literal keywords are the canonical way to express
// this and don't read against the design-token system.
const SOFT_MASK_VALUE = 'radial-gradient(ellipse 80% 80% at 100% 0%, black 15%, transparent 90%)';
const SOFT_MASK_STYLE = {
  maskImage: SOFT_MASK_VALUE,
  WebkitMaskImage: SOFT_MASK_VALUE
} as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProposalOutcomesPanel(t0) {
  const $ = _c(75);
  const {
    proposalPubkey,
    proposalCategory,
    outcomes,
    className,
    onSuggestModalOpenChange,
    isCreator: t1,
    isLoggedOut: t2,
    onRequireSignIn
  } = t0;
  const isCreator = t1 === undefined ? false : t1;
  const isLoggedOut = t2 === undefined ? false : t2;
  const t = useTranslations("launchpad.detail");
  const reducedMotion = useReducedMotion();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  let T0;
  let T1;
  let T2;
  let handleSuggestOutcome;
  let t10;
  let t11;
  let t12;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== className || $[1] !== hoveredIndex || $[2] !== isLoggedOut || $[3] !== onRequireSignIn || $[4] !== onSuggestModalOpenChange || $[5] !== outcomes || $[6] !== proposalCategory || $[7] !== proposalPubkey || $[8] !== reducedMotion || $[9] !== t) {
    const displayOutcomes = outcomes.toSorted(_temp);
    let t13;
    if ($[24] !== proposalCategory) {
      t13 = resolveProposalCategory(proposalCategory)?.emoji ?? FALLBACK_EMOJI;
      $[24] = proposalCategory;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    const categoryEmoji = t13;
    for (const o of displayOutcomes) {
      if (o.imageUrl !== "" && o.imageNsfw === false) {
        preload(toCustomGateway(o.imageUrl), {
          as: "image",
          fetchPriority: "low"
        });
      }
    }
    const hovered = hoveredIndex !== null ? displayOutcomes.find(o_0 => o_0.index === hoveredIndex) : undefined;
    const showBackground = hovered !== undefined && hovered.imageUrl !== "" && hovered.imageNsfw === false;
    let t14;
    if ($[26] !== isLoggedOut || $[27] !== onRequireSignIn || $[28] !== onSuggestModalOpenChange) {
      t14 = function handleSuggestOutcome() {
        if (isLoggedOut) {
          onRequireSignIn?.();
          return;
        }
        onSuggestModalOpenChange(true);
      };
      $[26] = isLoggedOut;
      $[27] = onRequireSignIn;
      $[28] = onSuggestModalOpenChange;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    handleSuggestOutcome = t14;
    if ($[30] !== className) {
      t12 = cn(className);
      $[30] = className;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    T2 = LazyMotion;
    t10 = domAnimation;
    t11 = true;
    T1 = Panel;
    t7 = "compact";
    t8 = "relative overflow-hidden";
    const t15 = showBackground ? <m.div key={hovered.index} className="absolute inset-0" initial={reducedMotion === true ? false : FADE_VARIANTS.initial} animate={FADE_VARIANTS.animate} exit={FADE_VARIANTS.exit} transition={reducedMotion === true ? {
      duration: 0
    } : MOTION_HOVER}><OptimizedImage variant="hero" src={hovered.imageUrl} alt="" preview={false} imageClassName="object-cover" /></m.div> : null;
    if ($[32] !== t15) {
      t9 = <div aria-hidden="true" className="pointer-events-none absolute top-0 right-0 size-[200px] overflow-hidden" style={SOFT_MASK_STYLE}><AnimatePresence mode="wait">{t15}</AnimatePresence></div>;
      $[32] = t15;
      $[33] = t9;
    } else {
      t9 = $[33];
    }
    T0 = PanelContent;
    t5 = "relative flex flex-col gap-4";
    let t16;
    if ($[34] !== t) {
      t16 = t("outcomesTitle");
      $[34] = t;
      $[35] = t16;
    } else {
      t16 = $[35];
    }
    if ($[36] !== t16) {
      t6 = <p className="text-body-lg font-medium text-foreground-primary">{t16}</p>;
      $[36] = t16;
      $[37] = t6;
    } else {
      t6 = $[37];
    }
    t3 = "m-0 grid min-w-0 list-none grid-cols-2 gap-2 p-0 @min-[768px]/proposal-detail:grid-cols-3 @min-[1280px]/proposal-detail:grid-cols-4";
    t4 = displayOutcomes.map(o_1 => <m.li key={o_1.index} layout={reducedMotion === true ? false : "position"} transition={reducedMotion === true ? {
      duration: 0
    } : MOTION_POSITION} className="min-w-0" onPointerEnter={() => setHoveredIndex(o_1.index)} onPointerLeave={() => setHoveredIndex(null)} onFocus={() => setHoveredIndex(o_1.index)} onBlur={() => setHoveredIndex(null)}><OutcomeCard variant="static" entityPubkey={proposalPubkey} index={o_1.index} label={o_1.label} colorOutcomes={displayOutcomes.map(_temp2)} percent={0} showPercent={false} imageUrl={o_1.imageUrl} imageNsfw={o_1.imageNsfw} imageAlt={o_1.label} imagePreview={o_1.imageUrl !== "" && o_1.imageNsfw === false} fallbackEmoji={categoryEmoji} /></m.li>);
    $[0] = className;
    $[1] = hoveredIndex;
    $[2] = isLoggedOut;
    $[3] = onRequireSignIn;
    $[4] = onSuggestModalOpenChange;
    $[5] = outcomes;
    $[6] = proposalCategory;
    $[7] = proposalPubkey;
    $[8] = reducedMotion;
    $[9] = t;
    $[10] = T0;
    $[11] = T1;
    $[12] = T2;
    $[13] = handleSuggestOutcome;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    T0 = $[10];
    T1 = $[11];
    T2 = $[12];
    handleSuggestOutcome = $[13];
    t10 = $[14];
    t11 = $[15];
    t12 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  let t13;
  if ($[38] !== t3 || $[39] !== t4) {
    t13 = <ul className={t3}>{t4}</ul>;
    $[38] = t3;
    $[39] = t4;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== isCreator || $[42] !== t) {
    t14 = isCreator ? t("outcomesAddPrompt") : t("outcomesSuggestPrompt");
    $[41] = isCreator;
    $[42] = t;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== t14) {
    t15 = <span className="text-foreground-tertiary">{t14}</span>;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== isCreator || $[47] !== t) {
    t16 = isCreator ? t("outcomesAddCta") : t("outcomesSuggestCta");
    $[46] = isCreator;
    $[47] = t;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== handleSuggestOutcome || $[50] !== t16) {
    t17 = <button type="button" onClick={handleSuggestOutcome} className="cursor-pointer font-medium text-foreground-primary transition-opacity hover:opacity-80">{t16}</button>;
    $[49] = handleSuggestOutcome;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== t15 || $[53] !== t17) {
    t18 = <div className="flex items-center gap-2 text-body">{t15}{t17}</div>;
    $[52] = t15;
    $[53] = t17;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== T0 || $[56] !== t13 || $[57] !== t18 || $[58] !== t5 || $[59] !== t6) {
    t19 = <T0 className={t5}>{t6}{t13}{t18}</T0>;
    $[55] = T0;
    $[56] = t13;
    $[57] = t18;
    $[58] = t5;
    $[59] = t6;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  let t20;
  if ($[61] !== T1 || $[62] !== t19 || $[63] !== t7 || $[64] !== t8 || $[65] !== t9) {
    t20 = <T1 density={t7} className={t8}>{t9}{t19}</T1>;
    $[61] = T1;
    $[62] = t19;
    $[63] = t7;
    $[64] = t8;
    $[65] = t9;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== T2 || $[68] !== t10 || $[69] !== t11 || $[70] !== t20) {
    t21 = <T2 features={t10} strict={t11}>{t20}</T2>;
    $[67] = T2;
    $[68] = t10;
    $[69] = t11;
    $[70] = t20;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== t12 || $[73] !== t21) {
    t22 = <div className={t12}>{t21}</div>;
    $[72] = t12;
    $[73] = t21;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  return t22;
}
function _temp2(outcome) {
  return {
    title: outcome.label,
    index: outcome.index
  };
}
function _temp(a, b) {
  return a.index - b.index;
}